import React, { useState } from 'react';
import {
  Button,
  Text,
  VStack,
  HStack,
  Icon,
  Box,
  Badge,
  Separator,
  IconButton,
  Collapsible,
  NativeSelect,
  DialogRoot,
  DialogBackdrop,
  DialogPositioner,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
} from '@chakra-ui/react';
import { FiLock, FiArrowRight, FiCheck, FiX, FiStar, FiChevronDown, FiChevronUp, FiHelpCircle } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { proceedToCheckout, type BillingInterval } from '../utils/billingCheckout';
import { plans, getPlanPrice, formatPlanPrice } from '../config/pricing';
import { useTranslation } from 'react-i18next';
import { Tooltip } from './ui/Tooltip';

export interface UpgradeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  feature: string;
  requiredPlan: 'basic' | 'premium' | 'pro';
  currentPlan?: string;
  description?: string;
  benefits?: string[];
  
  // Value bundles (for non-profile features)
  valueBundleOption?: {
    name: 'power' | 'professional';
    price: number;
    normalPrice: number;  // for savings calculation
    savings: number;
    includes: {
      tokens: number;
      tokensDescription: string;  // e.g., "~100 AI conversations"
      storageGb: number;
      wellnessGoals: number;
    };
    onPurchase: () => void | Promise<void>;
  };
  
  // Individual PAYG addon options (for non-profile features)
  addonOption?: {
    available: boolean;
    price: number;
    description: string;
    onPurchase: () => void | Promise<void>;
  };
  
  // Profile purchase option (one-time payment, quantity selector)
  profilePurchaseOption?: {
    available: boolean;
    pricePerProfile: number;
    onPurchase: (quantity: number) => void | Promise<void>;
  };
}

export const UpgradeDialog: React.FC<UpgradeDialogProps> = ({
  isOpen,
  onClose,
  feature,
  requiredPlan,
  currentPlan = 'free',
  description,
  benefits = [],
  valueBundleOption,
  addonOption,
  profilePurchaseOption,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showBenefits, setShowBenefits] = useState(false);
  const [showBundleDetails, setShowBundleDetails] = useState(false);
  const [profileQuantity, setProfileQuantity] = useState(1);
  const [isPurchasingProfile, setIsPurchasingProfile] = useState(false);
  const [isPurchasingBundle, setIsPurchasingBundle] = useState(false);
  const [isPurchasingAddon, setIsPurchasingAddon] = useState(false);
  const [purchaseError, setPurchaseError] = useState<string | null>(null);
  const [billingTerm, setBillingTerm] = useState<BillingInterval>('monthly');
  
  const planColors: Record<string, string> = {
    basic: 'blue',
    premium: 'purple',
  };
  
  const planConfig = plans.find(p => p.id === requiredPlan);
  const termOptions: Array<{ id: BillingInterval; label: string; price: string; note: string }> = planConfig
    ? [
        {
          id: 'monthly',
          label: 'Monthly',
          price: `${formatPlanPrice(getPlanPrice(planConfig, 'monthly'))}/mo`,
          note: 'Subscription · cancel anytime',
        },
        {
          id: 'quarterly',
          label: '3 months · pay once',
          price: `${formatPlanPrice(getPlanPrice(planConfig, 'quarterly'))} once`,
          note: 'No auto-renewal · nothing to cancel',
        },
      ]
    : [];
  const goesToCheckout = requiredPlan === 'basic' || requiredPlan === 'premium';

  const handleUpgrade = (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    onClose();
    if (goesToCheckout) {
      proceedToCheckout(requiredPlan, billingTerm, { source: 'upgrade_dialog' });
      return;
    }
    navigate('/plans-and-pricing');
  };

  const handleAddonPurchase = async () => {
    if (addonOption?.onPurchase) {
      setIsPurchasingAddon(true);
      setPurchaseError(null);
      try {
        await addonOption.onPurchase();
      } catch (e) {
        setIsPurchasingAddon(false);
        setPurchaseError(e instanceof Error ? e.message : 'Failed to start checkout. Please try again.');
      }
    }
  };

  const handleValueBundlePurchase = async () => {
    if (valueBundleOption?.onPurchase) {
      setIsPurchasingBundle(true);
      setPurchaseError(null);
      try {
        await valueBundleOption.onPurchase();
      } catch (e) {
        setIsPurchasingBundle(false);
        setPurchaseError(e instanceof Error ? e.message : 'Failed to start checkout. Please try again.');
      }
    }
  };

  return (
    <DialogRoot
      open={isOpen}
      onOpenChange={(e) => (e.open ? null : onClose())}
      placement="center"
      size={{ base: 'sm', md: 'xl' }}
      scrollBehavior="outside"
      motionPreset="scale"
    >
      <DialogBackdrop />
      <DialogPositioner>
        <DialogContent maxW={{ base: '90vw', md: '600px' }} w="100%">
          <DialogHeader bgGradient={`linear(to-r, ${planColors[requiredPlan]}.50, ${planColors[requiredPlan]}.100)`} borderBottomWidth="1px" borderColor="gray.200">
            <HStack justify="space-between" align="flex-start" mb={2}>
              <HStack gap={2}>
                <Box p={2} bg={`${planColors[requiredPlan]}.500`} borderRadius="lg">
                  <Icon as={FiLock} boxSize={4} color="white" />
                </Box>
                <VStack align="start" gap={0}>
                  <DialogTitle asChild>
                    <Text fontSize="lg" fontWeight="bold" color="gray.800">
                      Upgrade Required
                    </Text>
                  </DialogTitle>
                  <Badge colorScheme={planColors[requiredPlan]} fontSize="xs" px={2} py={0.5} borderRadius="full">
                    {requiredPlan.charAt(0).toUpperCase() + requiredPlan.slice(1)} Plan
                  </Badge>
                </VStack>
              </HStack>
              <IconButton aria-label="Close dialog" variant="ghost" size="sm" onClick={onClose} _hover={{ bg: 'blackAlpha.100' }}>
                <FiX />
              </IconButton>
            </HStack>
            {description && (
              <Text color="gray.700" fontSize="sm" mt={2}>
                {description}
              </Text>
            )}
          </DialogHeader>

          <DialogBody p={5} py={4}>
            <VStack gap={3} align="stretch">
              {/* Benefits Section - Collapsible */}
              {benefits.length > 0 && (
                <Box>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowBenefits(!showBenefits)}
                    width="full"
                    justifyContent="space-between"
                    fontWeight="semibold"
                    color="gray.700"
                    _hover={{ bg: 'gray.100' }}
                  >
                    <HStack gap={2}>
                      <Text>Additional benefits</Text>
                      <Icon as={showBenefits ? FiChevronUp : FiChevronDown} />
                    </HStack>
                  </Button>
                  <Collapsible.Root open={showBenefits}>
                    <Collapsible.Content>
                      <VStack align="stretch" gap={2} mt={3} pl={2}>
                        {benefits.map((benefit, idx) => (
                          <HStack key={idx} gap={2}>
                            <Icon as={FiCheck} color="green.500" boxSize={4} flexShrink={0} />
                            <Text fontSize="sm" color="gray.700">{benefit}</Text>
                          </HStack>
                        ))}
                      </VStack>
                    </Collapsible.Content>
                  </Collapsible.Root>
                </Box>
              )}

              {/* Pricing Options */}
              <VStack gap={3} align="stretch">
                {/* Profile Purchase - Show FIRST if available (one-time payment) */}
                {profilePurchaseOption?.available && (
                  <Box
                    p={4}
                    bg="white"
                    borderRadius="lg"
                    borderWidth="3px"
                    borderColor="green.400"
                    position="relative"
                    boxShadow="md"
                  >
                    <HStack
                      position="absolute"
                      top={-3}
                      left="50%"
                      transform="translateX(-50%)"
                      bg="green.500"
                      color="white"
                      px={4}
                      py={1}
                      borderRadius="full"
                      gap={1}
                    >
                      <Icon as={FiStar} boxSize={3} />
                      <Text fontSize="xs" fontWeight="bold">
                        ONE-TIME PAYMENT
                      </Text>
                    </HStack>
                    
                    <VStack gap={3} align="stretch" mt={2}>
                      <HStack justify="space-between" align="center">
                        <VStack align="start" gap={1}>
                          <Text fontSize="md" fontWeight="bold" color="gray.900">
                            Purchase Additional Profiles
                          </Text>
                          <Text fontSize="sm" color="gray.600">
                            Add more profiles to your account (permanent)
                          </Text>
                        </VStack>
                        <VStack align="end" gap={0}>
                          <HStack gap={1} align="baseline">
                            <Text fontSize="2xl" fontWeight="bold" color="green.600">
                              ${(profilePurchaseOption.pricePerProfile * profileQuantity).toFixed(2)}
                            </Text>
                          </HStack>
                          <Text fontSize="xs" color="gray.500">
                            ${profilePurchaseOption.pricePerProfile} × {profileQuantity}
                          </Text>
                        </VStack>
                      </HStack>
                      
                      <HStack gap={2} align="center">
                        <Text fontSize="sm" color="gray.700" minW="100px">
                          Quantity:
                        </Text>
                        <NativeSelect.Root
                          size="md"
                          defaultValue={String(profileQuantity)}
                          onChange={(e) => setProfileQuantity(parseInt((e.target as HTMLSelectElement).value))}
                          flex={1}
                        >
                          <NativeSelect.Field>
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                              <option key={num} value={num}>
                                {num} profile{num > 1 ? 's' : ''}
                              </option>
                            ))}
                          </NativeSelect.Field>
                        </NativeSelect.Root>
                      </HStack>
                      
                      <Button
                        colorScheme="green"
                        onClick={async () => {
                          // Don't close dialog - onPurchase handles redirect to Stripe
                          setIsPurchasingProfile(true)
                          setPurchaseError(null)
                          try {
                            await profilePurchaseOption.onPurchase(profileQuantity)
                          } catch (e) {
                            setIsPurchasingProfile(false)
                            setPurchaseError(e instanceof Error ? e.message : 'Failed to start checkout. Please try again.')
                          }
                        }}
                        size="md"
                        width="100%"
                        loading={isPurchasingProfile}
                        disabled={isPurchasingProfile}
                      >
                        <HStack gap={2}>
                          <Text>{isPurchasingProfile ? 'Redirecting to checkout...' : `Purchase ${profileQuantity} Profile${profileQuantity > 1 ? 's' : ''}`}</Text>
                          {!isPurchasingProfile && <FiArrowRight />}
                        </HStack>
                      </Button>
                    </VStack>
                  </Box>
                )}

                {/* Value Bundle - Show FIRST (per spec: RECOMMENDED) */}
                    {valueBundleOption && (
                      <Box
                        p={4}
                        bg="white"
                        borderRadius="lg"
                        borderWidth="3px"
                        borderColor="purple.400"
                        position="relative"
                        boxShadow="md"
                      >
                        <HStack
                          position="absolute"
                          top={-3}
                          left="50%"
                          transform="translateX(-50%)"
                          bg="purple.500"
                          color="white"
                          px={4}
                          py={1}
                          borderRadius="full"
                          gap={1}
                        >
                          <Icon as={FiStar} boxSize={3} />
                          <Text fontSize="xs" fontWeight="bold">
                            BEST VALUE
                          </Text>
                        </HStack>
                        
                        <VStack gap={3} align="stretch" mt={2}>
                          <HStack justify="space-between" align="center">
                            <VStack align="start" gap={0}>
                              <Text fontSize="md" fontWeight="bold" color="gray.900">
                                {valueBundleOption.name === 'power' ? 'Power User Bundle' : 'Professional Bundle'}
                              </Text>
                              <HStack gap={2} align="baseline">
                                <Text fontSize="2xl" fontWeight="bold" color="purple.600">
                                  ${valueBundleOption.price}
                                </Text>
                                <Text fontSize="sm" color="gray.600">/mo</Text>
                                <Text fontSize="xs" color="gray.500" textDecoration="line-through">
                                  ${valueBundleOption.normalPrice}
                                </Text>
                                <Badge colorScheme="green" fontSize="xs">
                                  Save ${valueBundleOption.savings}
                                </Badge>
                              </HStack>
                            </VStack>
                            <Button
                              variant="ghost"
                              size="xs"
                              onClick={() => setShowBundleDetails(!showBundleDetails)}
                            >
                              <HStack gap={1}>
                                <Text>Details</Text>
                                <Icon as={showBundleDetails ? FiChevronUp : FiChevronDown} />
                              </HStack>
                            </Button>
                          </HStack>
                          
                          <Collapsible.Root open={showBundleDetails}>
                            <Collapsible.Content>
                              <Box bg="gray.50" p={3} borderRadius="md" mb={2}>
                                <VStack align="stretch" gap={2}>
                                  <HStack fontSize="sm" color="gray.700">
                                    <Icon as={FiCheck} color="purple.500" boxSize={4} />
                                    <HStack gap={1}>
                                      <Text fontWeight="medium">
                                        {valueBundleOption.includes.tokens.toLocaleString()} credits {valueBundleOption.includes.tokensDescription}
                                      </Text>
                                      <Tooltip
                                        label={t('billing.add_tokens.token_definition', 'Conversation credits power Coach sessions, check-ins, and reports. Every conversation with Coach uses credits based on what you write, what Coach writes back, the conversation history, and actions Coach takes.\n\nApproximately 1 credit = 0.75 words, or about 1,000 credits = 750 words.')}
                                        placement="top"
                                      >
                                        <IconButton
                                          aria-label={t('billing.add_tokens.what_are_tokens', 'What are conversation credits?')}
                                          variant="ghost"
                                          size="xs"
                                          color="gray.400"
                                          _hover={{ color: 'gray.600' }}
                                          minW="auto"
                                          h="auto"
                                          p={0}
                                        >
                                          <FiHelpCircle />
                                        </IconButton>
                                      </Tooltip>
                                    </HStack>
                                  </HStack>
                                  <HStack fontSize="sm" color="gray.700">
                                    <Icon as={FiCheck} color="purple.500" boxSize={4} />
                                    <Text fontWeight="medium">{valueBundleOption.includes.storageGb}GB storage</Text>
                                  </HStack>
                                  <HStack fontSize="sm" color="gray.700">
                                    <Icon as={FiCheck} color="purple.500" boxSize={4} />
                                    <Text fontWeight="medium">+{valueBundleOption.includes.wellnessGoals} wellness goals (bonus)</Text>
                                  </HStack>
                                </VStack>
                              </Box>
                            </Collapsible.Content>
                          </Collapsible.Root>
                          
                          <Button
                            colorScheme="purple"
                            onClick={handleValueBundlePurchase}
                            size="md"
                            width="100%"
                            loading={isPurchasingBundle}
                            disabled={isPurchasingBundle}
                          >
                            <HStack gap={2}>
                              <Text>{isPurchasingBundle ? 'Redirecting to checkout...' : `Get Bundle - $${valueBundleOption.price}/mo`}</Text>
                              {!isPurchasingBundle && <FiArrowRight />}
                            </HStack>
                          </Button>
                        </VStack>
                      </Box>
                    )}

                    {/* Individual Addon - Show as secondary option */}
                    {addonOption?.available && (
                      <Box
                        p={3}
                        bg="gray.50"
                        borderRadius="lg"
                        borderWidth="2px"
                        borderColor="gray.200"
                      >
                        <HStack gap={3} justify="space-between" align="center">
                          <VStack align="start" gap={0} flex={1}>
                            <Text fontSize="sm" fontWeight="bold" color="gray.800">
                              {valueBundleOption ? 'Just need one profile?' : 'Quick Add-On'}
                            </Text>
                            <Text fontSize="xs" color="gray.600">
                              {addonOption.description}
                            </Text>
                          </VStack>
                          <Button
                            size="sm"
                            colorScheme="green"
                            variant={valueBundleOption ? 'outline' : 'solid'}
                            onClick={handleAddonPurchase}
                            flexShrink={0}
                            loading={isPurchasingAddon}
                            disabled={isPurchasingAddon}
                          >
                            {isPurchasingAddon ? 'Redirecting...' : `$${addonOption.price}/mo`}
                          </Button>
                        </HStack>
                      </Box>
                    )}

                    {/* Base Plan Option */}
                    {!valueBundleOption && !addonOption?.available && (
                      <Box
                        p={4}
                        bg={`${planColors[requiredPlan]}.50`}
                        borderRadius="lg"
                        borderWidth="2px"
                        borderColor={`${planColors[requiredPlan]}.200`}
                      >
                        <VStack align="stretch" gap={3}>
                          <Text fontSize="xs" color="gray.600" fontWeight="medium">
                            {requiredPlan.charAt(0).toUpperCase() + requiredPlan.slice(1)} Plan · how do you want to pay?
                          </Text>
                          {termOptions.map(option => {
                            const selected = billingTerm === option.id;
                            return (
                              <Box
                                key={option.id}
                                as="button"
                                textAlign="left"
                                p={3}
                                borderRadius="md"
                                borderWidth="2px"
                                borderColor={selected ? `${planColors[requiredPlan]}.500` : 'gray.200'}
                                bg={selected ? 'white' : 'transparent'}
                                onClick={() => setBillingTerm(option.id)}
                              >
                                <HStack justify="space-between" align="center">
                                  <VStack align="start" gap={0}>
                                    <Text fontSize="sm" fontWeight="semibold" color="gray.800">{option.label}</Text>
                                    <Text fontSize="xs" color="gray.600">{option.note}</Text>
                                  </VStack>
                                  <Text fontSize="lg" fontWeight="bold" color={`${planColors[requiredPlan]}.700`}>
                                    {option.price}
                                  </Text>
                                </HStack>
                              </Box>
                            );
                          })}
                        </VStack>
                      </Box>
                    )}
              </VStack>
              
              {/* Error Display */}
              {purchaseError && (
                <Box p={3} bg="red.50" borderRadius="md" borderWidth="1px" borderColor="red.200">
                  <Text color="red.700" fontSize="sm">{purchaseError}</Text>
                </Box>
              )}
            </VStack>
          </DialogBody>

          <DialogFooter p={5} pt={4} bg="gray.50" borderTopWidth="1px" borderColor="gray.200">
            <HStack gap={3} width="100%">
              <Button 
                variant="ghost" 
                onClick={onClose}
                size="md"
                flex={1}
                color="gray.600"
              >
                Maybe Later
              </Button>
              <Button
                colorScheme={planColors[requiredPlan]}
                onClick={handleUpgrade}
                size="md"
                flex={1}
              >
                <HStack gap={2}>
                  <Text>
                    {valueBundleOption || addonOption?.available
                      ? 'View All Plans'
                      : goesToCheckout
                        ? `Upgrade to ${requiredPlan.charAt(0).toUpperCase() + requiredPlan.slice(1)}`
                        : 'View Plans'}
                  </Text>
                  <FiArrowRight />
                </HStack>
              </Button>
            </HStack>
          </DialogFooter>
        </DialogContent>
      </DialogPositioner>
    </DialogRoot>
  );
};
